import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TradeDistribution } from '@pnl/shared';
import { api } from '../../lib/api';
import { useChartColors } from '../../lib/chartColors';
import { formatBrl } from '../../lib/format';

interface Props {
  accountId: string;
}

const WIN = '#00C9A7';
const LOSS = '#E84040';
const EVEN = '#5A5A5A';

interface BarRow {
  symbol: string;
  pnl: number;
  trades: number;
}

interface BarTooltipPayload {
  payload: BarRow;
}

function BarTooltip({ active, payload }: { active?: boolean; payload?: BarTooltipPayload[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]!.payload;
  return (
    <div className="border-ink-700 bg-ink-900 rounded-md border px-3 py-2 text-xs shadow-lg">
      <p className="text-ink-100 font-semibold">{row.symbol}</p>
      <p className={'mt-1 tabular-nums ' + (row.pnl >= 0 ? 'text-success-300' : 'text-danger-300')}>
        {formatBrl(row.pnl, { signed: true })}
      </p>
      <p className="text-ink-400">{row.trades} operações</p>
    </div>
  );
}

export function DistributionCharts({ accountId }: Props) {
  const chart = useChartColors();
  const { data, isLoading } = useQuery<TradeDistribution>({
    queryKey: ['distribution', accountId],
    queryFn: () => api.getTradeDistribution(accountId),
  });

  if (isLoading) {
    return <p className="text-ink-400 px-5 py-10 text-center text-sm">Carregando gráficos…</p>;
  }

  const total = (data?.winCount ?? 0) + (data?.lossCount ?? 0) + (data?.breakEvenCount ?? 0);
  if (!data || total === 0) {
    return (
      <p className="text-ink-400 px-5 py-10 text-center text-sm">
        Sem operações suficientes para gerar a distribuição. Aparece após a importação do CSV.
      </p>
    );
  }

  const winRate = total > 0 ? (data.winCount / total) * 100 : 0;
  const pieData = [
    { name: 'Ganhos', value: data.winCount, color: WIN },
    { name: 'Perdas', value: data.lossCount, color: LOSS },
    ...(data.breakEvenCount > 0
      ? [{ name: 'Zerados', value: data.breakEvenCount, color: EVEN }]
      : []),
  ];

  const barRows: BarRow[] = data.bySymbol.map((s) => ({
    symbol: s.symbol,
    pnl: s.pnlCents,
    trades: s.trades,
  }));

  return (
    <div className="grid gap-5 p-5 lg:grid-cols-2">
      {/* Donut ganhos x perdas */}
      <div className="border-ink-800 rounded-xl border p-4">
        <p className="text-ink-100 text-sm font-semibold">Ganhos × Perdas</p>
        <div className="relative mt-2 h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={62}
                outerRadius={90}
                paddingAngle={2}
                stroke="none"
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: '#242424',
                  border: '1px solid #2E2E2E',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-ink-100 text-2xl font-bold tabular-nums">
              {winRate.toFixed(0)}%
            </span>
            <span className="text-2xs text-ink-400">taxa de acerto</span>
          </div>
        </div>
        <div className="mt-3 flex justify-center gap-4 text-xs">
          <span className="text-success-300 flex items-center gap-1.5">
            <span className="bg-success-500 h-2 w-2 rounded-full" /> Ganhos: {data.winCount}
          </span>
          <span className="text-danger-300 flex items-center gap-1.5">
            <span className="bg-danger-500 h-2 w-2 rounded-full" /> Perdas: {data.lossCount}
          </span>
        </div>
        <div className="border-ink-800 mt-3 grid grid-cols-2 gap-2 border-t pt-3 text-center">
          <div>
            <p className="text-2xs text-ink-400 uppercase tracking-wider">Total ganho</p>
            <p className="text-success-300 text-sm font-bold tabular-nums">
              {formatBrl(data.totalWinCents)}
            </p>
          </div>
          <div>
            <p className="text-2xs text-ink-400 uppercase tracking-wider">Total perdido</p>
            <p className="text-danger-300 text-sm font-bold tabular-nums">
              {formatBrl(data.totalLossCents)}
            </p>
          </div>
        </div>
      </div>

      {/* Barras por ativo */}
      <div className="border-ink-800 rounded-xl border p-4">
        <p className="text-ink-100 text-sm font-semibold">PnL por ativo</p>
        {barRows.length === 0 ? (
          <p className="text-ink-400 py-16 text-center text-sm">Sem dados por ativo.</p>
        ) : (
          <div className="mt-2 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barRows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="symbol"
                  stroke={chart.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke={chart.axis}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) =>
                    `${(v / 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
                  }
                  width={60}
                />
                <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                  {barRows.map((row) => (
                    <Cell key={row.symbol} fill={row.pnl >= 0 ? WIN : LOSS} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
